import { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Container, Grid, Stack, Typography, Box, Card } from '@mui/material';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import { NOT_AVAILABLE } from '../Global/Constants';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getMasterListType, saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { transformToSelectOptions } from '../../utils/CommonFunction';
import CustomSelect from '../../components/CustomSelect';
import CustomInput from '../../components/CustomInput';
import { BankOpsApi } from '../../utils/Apis';

const rejectionSchema = yup.object().shape({
  rejectedReasonTypeId: yup.string().nullable().required('Reject Reason is required'),
  comment: yup.string().required('Comments are required').max(500)
});

const sanctionSchema = yup.object().shape({
  amountSanctioned: yup
    .number()
    .typeError('Must be a number')
    .required('Amount Sanctioned is required'),
  processingFeePercentage: yup.number().notRequired().nullable(),
  totalProcessingFee: yup
    .number()
    .typeError('Must be a number')
    .required('Total Processing Fee is required'),
  bankWaiver: yup.number().typeError('Must be a number').required('Bank Waiver is required'),
  applicantShare: yup
    .number()
    .typeError('Must be a number')
    .required('Applicant Share is required')
    .min(0),
  companyShare: yup
    .number()
    .typeError('Must be a number')
    .required('Company Share is required')
    .min(0),
  processingFeeStatusId: yup.string().nullable().required('Status is required'),
  feeToBeDeductedFromInvoice: yup
    .number()
    .typeError('Must be a number')
    .required('Fee to be deducted is required'),
  payout: yup.number().typeError('Must be a number').required('Payout is required'),
  brokerAmount: yup.number().typeError('Must be a number').required('Broker Amount is required'),
  netRevenue: yup.number().typeError('Must be a number').required('Net Revenue is required')
});

export default function DecisionBankops() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state: rowId } = useLocation();
  const { LoginData } = useLoginSelector();
  const [masterOptions, setMasterOptions] = useState({ rejection: [], processing: [] });

  const isSanctioned = useMemo(
    () => rowId?.logInStatusName?.toLowerCase() === 'sanctioned',
    [rowId]
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(isSanctioned ? sanctionSchema : rejectionSchema)
  });

  const payout = watch('payout');
  const brokerAmount = watch('brokerAmount');

  useEffect(() => {
    const net = (Number(payout) || 0) - (Number(brokerAmount) || 0);
    setValue('netRevenue', net);
  }, [payout, brokerAmount, setValue]);

  // 🔥 Log errors for debugging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('❌ Form Validation Errors:', errors);
    }
  }, [errors]);

  useEffect(() => {
    (async () => {
      const [proc, rej] = await Promise.all([
        dispatch(getMasterListType('PROCESSING_FEE_STATUS_TYPE')),
        dispatch(getMasterListType('REJECTION_REASON_TYPE'))
      ]);
      setMasterOptions({
        processing: transformToSelectOptions(proc?.data?.masterTypes || [], 'typeName', 'typeId'),
        rejection: transformToSelectOptions(rej?.data?.masterTypes || [], 'typeName', 'typeId')
      });
    })();
  }, [dispatch]);

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        applicationId: rowId?.applicationId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        ...(isSanctioned
          ? {
              bankOpsDecisionInfoDisbursementRequestModel: {
                bankOpsDecisionInfoDisbursementDisbursementDetailsRequestModel: {
                  amountSanctioned: data.amountSanctioned
                },
                bankOpsDecisionInfoDisbursementProcessingFeeDetailsRequestModel: { ...data },
                bankOpsDecisionInfoDisbursementCommissionSharingRequestModel: {
                  leadOwnerId: rowId?.leadOwnerId,
                  leadOwner: rowId?.leadOwnerName
                }
              }
            }
          : {
              bankOpsDecisionInfoRejectionRequestModel: {
                bankOpsDecisionInfoRejectionRejectionDetailsRequestModel: { ...data }
              }
            })
      };
      const res = await dispatch(saveApiData(BankOpsApi.saveDecisionBankInfo, payload));
      if (res?.status === 'success') navigate('/bankops');
    },
    [LoginData, rowId, isSanctioned, dispatch, navigate]
  );

  return (
    <Page title="Partner Management System || Log In">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Application Closure Details" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <StatusBox status={rowId?.logInStatusName} />
          <form
            onSubmit={handleSubmit(onSubmit, (err) => {
              console.log('❌ handleSubmit rejected. Errors:', err);
            })}
          >
            {isSanctioned ? (
              <Box>
                <Card
                  sx={{
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                    p: 3,
                    mb: 3
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: '#13519d', mb: 2.5 }}
                  >
                    Disbursement Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomInput
                        name="amountSanctioned"
                        label="Amount Sanctioned"
                        control={control}
                        errors={errors}
                        isRequired
                      />
                    </Grid>
                  </Grid>
                </Card>

                <Card
                  sx={{
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                    p: 3,
                    mb: 3
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: '#13519d', mb: 2.5 }}
                  >
                    Processing Fee Details
                  </Typography>
                  <Grid container spacing={3}>
                    {['totalProcessingFee', 'bankWaiver', 'applicantShare', 'companyShare'].map(
                      (f) => (
                        <Grid item xs={12} sm={6} md={4} key={f}>
                          <CustomInput
                            name={f}
                            label={f.replace(/([A-Z])/g, ' $1').trim()}
                            control={control}
                            errors={errors}
                            isRequired
                          />
                        </Grid>
                      )
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomSelect
                        name="processingFeeStatusId"
                        label="Status"
                        control={control}
                        errors={errors}
                        options={masterOptions.processing}
                        isRequired
                      />
                    </Grid>

                    {['feeToBeDeductedFromInvoice', 'payout', 'brokerAmount', 'netRevenue'].map(
                      (f) => (
                        <Grid item xs={12} sm={6} md={4} key={f}>
                          <CustomInput
                            name={f}
                            label={f.replace(/([A-Z])/g, ' $1').trim()}
                            control={control}
                            errors={errors}
                            isRequired
                            disabled={f === 'netRevenue'}
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                </Card>
              </Box>
            ) : (
              <Box>
                <Card
                  sx={{
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                    p: 3,
                    mb: 3
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: '#13519d', mb: 2.5 }}
                  >
                    Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <CustomSelect
                        name="rejectedReasonTypeId"
                        label="Reject Reason"
                        control={control}
                        errors={errors}
                        options={masterOptions.rejection}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomInput
                        name="comment"
                        label="Comments"
                        type="textarea"
                        multiline
                        rows={2}
                        control={control}
                        errors={errors}
                        isRequired
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            )}
            <Box textAlign="right" mt={3}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  backgroundColor: '#13519d',
                  borderRadius: '8px',
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(19, 81, 157, 0.24)',
                  '&:hover': { backgroundColor: '#0d3b75' }
                }}
              >
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </Container>
    </Page>
  );
}

function StatusBox({ status }) {
  return (
    <Box textAlign="right" mb={2}>
      <Box className="statusBox" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>
          Status:
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#13519d', fontWeight: 700, fontSize: 13 }}>
          {status || NOT_AVAILABLE}
        </Typography>
      </Box>
    </Box>
  );
}
